import DocsLayout, { FeatureCard, Callout } from '../../components/docs/DocsLayout';
import { Zap, Shield, Layers, Globe } from 'lucide-react';

export default function DocsIntroduction() {
  return (
    <DocsLayout 
      title="Introduction" 
      description="Welcome to AIBL, the AI Banking Layer for autonomous agents"
    >
      <section className="space-y-6">
        <p className="text-white/70 leading-relaxed">
          AIBL (AI Banking Layer) is a DeFi infrastructure platform that enables developers to create and manage autonomous AI agents with ERC-4337 smart account wallets. Our platform provides the tools and permissions framework for agents to interact with on-chain liquidity across 20+ blockchains.
        </p>

        <Callout type="info" title="What is AIBL?">
          Think of AIBL as the financial operating system for AI agents. It handles wallet management, transaction signing, multi-sig security, and cross-chain operations so your agents can focus on what they do best.
        </Callout>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Key Features</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 not-prose">
          <FeatureCard
            icon={<Zap size={24} className="text-yellow-400" />}
            title="Autonomous Agents"
            description="Deploy AI agents that can execute DeFi operations independently based on your configured permissions."
            href="/docs/agents"
          />
          <FeatureCard
            icon={<Shield size={24} className="text-green-400" />}
            title="Multi-Sig Security"
            description="Protect high-value operations with Gnosis Safe integration and customizable approval thresholds."
            href="/docs/multisig"
          />
          <FeatureCard
            icon={<Layers size={24} className="text-blue-400" />}
            title="Plugin System"
            description="Extend agent capabilities with our modular plugin architecture for swaps, bridges, and more."
            href="/docs/plugins"
          />
          <FeatureCard
            icon={<Globe size={24} className="text-purple-400" />}
            title="Multi-Chain Support"
            description="Operate seamlessly across Ethereum, Base, Arbitrum, Polygon, Optimism, and 15+ more chains."
            href="/docs/wallets"
          />
        </div>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">How It Works</h2>
        
        <ol className="space-y-4 text-white/70">
          <li className="flex gap-4">
            <span className="flex-shrink-0 w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-sm font-bold text-white">1</span>
            <div>
              <strong className="text-white">Create an Agent</strong>
              <p>Deploy a new AI agent with an ERC-4337 smart account wallet. Each agent gets its own isolated wallet and permission set.</p>
            </div>
          </li>
          <li className="flex gap-4">
            <span className="flex-shrink-0 w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-sm font-bold text-white">2</span>
            <div>
              <strong className="text-white">Configure Permissions</strong>
              <p>Use our plugin system to define what operations your agent can perform. Set spending limits, allowed protocols, and whitelisted addresses.</p>
            </div>
          </li>
          <li className="flex gap-4">
            <span className="flex-shrink-0 w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-sm font-bold text-white">3</span>
            <div>
              <strong className="text-white">Deploy and Monitor</strong>
              <p>Your agent operates autonomously within its permission boundaries. Monitor activity and adjust settings as needed through our dashboard.</p>
            </div>
          </li>
        </ol>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Quick Links</h2>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 not-prose">
          <a href="/docs/quickstart" className="flex items-center gap-3 p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors border border-white/10">
            <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
              <Zap size={18} className="text-green-400" />
            </div>
            <div>
              <p className="font-medium text-white">Quick Start Guide</p>
              <p className="text-sm text-white/50">Get running in 5 minutes</p>
            </div>
          </a>
          <a href="/docs/api/endpoints" className="flex items-center gap-3 p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors border border-white/10">
            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <Layers size={18} className="text-blue-400" />
            </div>
            <div>
              <p className="font-medium text-white">API Reference</p>
              <p className="text-sm text-white/50">Explore our endpoints</p>
            </div>
          </a>
        </div>
      </section>
    </DocsLayout>
  );
}
